/**
 * dbtabellen/StrukturWerkzeuge – Welle 97: Struktur bearbeiten in „DB-Tabellen 2".
 *
 * Nur für die neue Datenbank (imho_v2). Das Backend verweigert diese Aufrufe für die alte.
 *  • Neue Spalten kommen immer ans Ende — Pivots und Sheets hängen an der Reihenfolge.
 *  • Gelöschte Spalten landen mit ihren Werten im Papierkorb und lassen sich zurückholen.
 *  • „Tabelle löschen" benennt nur um; die Daten bleiben in der DB liegen.
 */
import React, { useCallback, useEffect, useState } from "react";

const S = {
  knopf: { padding: "6px 12px", margin: 0, border: "1px solid var(--color-gray-300)", borderRadius: 6, background: "var(--color-gray-100)", color: "var(--color-gray-800)", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600 },
  prim: { padding: "6px 12px", margin: 0, border: "none", borderRadius: 6, background: "var(--color-info)", color: "#fff", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600 },
  rot: { padding: "6px 12px", margin: 0, border: "none", borderRadius: 6, background: "var(--color-error)", color: "#fff", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600 },
  mini: { padding: "2px 8px", margin: "0 0 0 4px", border: "1px solid var(--color-gray-300)", borderRadius: 5, background: "var(--bg-card)", color: "var(--color-gray-700)", cursor: "pointer", fontSize: "0.74rem", fontWeight: 600 },
  input: { padding: "5px 8px", border: "1px solid var(--color-gray-300)", borderRadius: 6, fontSize: "0.82rem", background: "var(--bg-card)", color: "var(--color-gray-800)" },
  kasten: { border: "1px solid var(--color-gray-300)", borderRadius: 8, padding: 12, margin: "10px 0", background: "var(--bg-card)", fontSize: "0.85rem" },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  dialog: { background: "var(--bg-card)", borderRadius: 10, padding: 20, width: 520, maxWidth: "92vw", maxHeight: "84vh", overflow: "auto", textAlign: "left" },
  err: { background: "var(--color-error-bg)", border: "1px solid var(--color-error)", borderRadius: 6, padding: "8px 12px", color: "var(--color-error)", margin: "8px 0", fontSize: "0.85rem" },
  ok: { background: "var(--color-success-bg)", border: "1px solid var(--color-success)", borderRadius: 6, padding: "8px 12px", color: "var(--color-success)", margin: "8px 0", fontSize: "0.85rem" },
  th: { textAlign: "left", padding: "4px 8px", borderBottom: "1px solid var(--color-gray-300)", fontSize: "0.76rem", color: "var(--color-gray-600)" },
  td: { padding: "4px 8px", borderBottom: "1px solid var(--color-gray-200)", fontSize: "0.82rem", verticalAlign: "middle" },
};

async function ruf(url, method = "GET", body) {
  const init = { method, headers: {} };
  if (body !== undefined) { init.headers["Content-Type"] = "application/json"; init.body = JSON.stringify(body); }
  const r = await fetch(url, init);
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || r.statusText);
  return d;
}

/* ── Spalten einer Tabelle: hinzufügen, umbenennen, löschen ─────────────── */
export function SpaltenPanel({ B, tabelle, spaltenMeta, onGeaendert, onSchliessen }) {
  const [typen, setTypen] = useState({ typen: [], gruppen: [] });
  const [neu, setNeu] = useState({ name: "", typ: "text", kommentar: "", gruppe: "" });
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState("");
  const [meldung, setMeldung] = useState("");
  const [umbenennen, setUmbenennen] = useState(null);      // { spalte, wert }
  const [loeschen, setLoeschen] = useState(null);          // Vorschau vom Server

  useEffect(() => { ruf(`${B}/struktur/typen?tabelle=${tabelle}`).then(setTypen).catch((e) => setFehler(e.message)); }, [B, tabelle]);

  const lauf = async (fn, text) => {
    setBusy(true); setFehler(""); setMeldung("");
    try { await fn(); setMeldung(text); onGeaendert(); }
    catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  const hinzufuegen = () => lauf(async () => {
    await ruf(`${B}/table/${tabelle}/column`, "POST", {
      name: neu.name, typ: neu.typ, kommentar: neu.kommentar || null, gruppe: neu.gruppe || null,
    });
    setNeu({ name: "", typ: "text", kommentar: "", gruppe: neu.gruppe });
  }, `Spalte „${neu.name.toLowerCase()}" steht jetzt ganz hinten.`);

  const umbenennenSpeichern = () => lauf(async () => {
    const r = await ruf(`${B}/table/${tabelle}/column/${umbenennen.spalte}`, "PUT", { neuer_name: umbenennen.wert });
    setUmbenennen(null);
    if (r.ansichten_pruefen?.length) throw new Error(`Umbenannt. Diese Ansichten bitte prüfen: ${r.ansichten_pruefen.join(", ")}`);
  }, "Spalte umbenannt.");

  const loeschVorschau = async (spalte) => {
    setFehler(""); setMeldung("");
    try { setLoeschen(await ruf(`${B}/table/${tabelle}/column/${spalte}`, "DELETE")); }
    catch (e) { setFehler(String(e.message || e)); }
  };

  const loeschenJetzt = (mitAnsichten) => lauf(async () => {
    await ruf(`${B}/table/${tabelle}/column/${loeschen.spalte}?bestaetigt=1${mitAnsichten ? "&ansichten_entfernen=1" : ""}`, "DELETE");
    setLoeschen(null);
  }, "Spalte gelöscht — die Werte liegen im Papierkorb.");

  return (
    <div style={S.kasten}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <strong style={{ flex: 1 }}>Spalten von „{tabelle}"</strong>
        <button style={S.mini} onClick={onSchliessen}>schließen</button>
      </div>
      {fehler && <div style={S.err}>{fehler}</div>}
      {meldung && <div style={S.ok}>{meldung}</div>}

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
        <input style={{ ...S.input, width: 190 }} placeholder="neue_spalte" value={neu.name}
               onChange={(e) => setNeu({ ...neu, name: e.target.value })} />
        <select style={S.input} value={neu.typ} onChange={(e) => setNeu({ ...neu, typ: e.target.value })}>
          {typen.typen.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        <input style={{ ...S.input, width: 150 }} list="krv-spaltengruppen" placeholder="Gruppe (z. B. Miete)" value={neu.gruppe}
               onChange={(e) => setNeu({ ...neu, gruppe: e.target.value })} />
        <datalist id="krv-spaltengruppen">{(typen.gruppen || []).map((g) => <option key={g} value={g} />)}</datalist>
        <input style={{ ...S.input, flex: "1 1 180px" }} placeholder="Wofür ist die Spalte? (optional)" value={neu.kommentar}
               onChange={(e) => setNeu({ ...neu, kommentar: e.target.value })} />
        <button style={S.prim} disabled={busy || !neu.name.trim()} onClick={hinzufuegen}>＋ Spalte hinzufügen</button>
      </div>
      <div style={{ fontSize: "0.76rem", color: "var(--color-gray-600)", marginBottom: 8 }}>
        Neue Spalten kommen immer ans Ende — so bleiben Pivots und verknüpfte Google-Tabellen stabil.
        Namen: Kleinbuchstaben, Ziffern und „_".
      </div>

      <div style={{ maxHeight: 300, overflow: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr><th style={S.th}>#</th><th style={S.th}>Spalte</th><th style={S.th}>Gruppe</th><th style={S.th}>Typ</th><th style={S.th}></th></tr></thead>
          <tbody>
            {(spaltenMeta || []).map((c, i) => (
              <tr key={c.field}>
                <td style={{ ...S.td, color: "var(--color-gray-500)" }}>{i + 1}</td>
                <td style={S.td}>
                  {umbenennen?.spalte === c.field ? (
                    <>
                      <input style={{ ...S.input, width: 190 }} autoFocus value={umbenennen.wert}
                             onChange={(e) => setUmbenennen({ ...umbenennen, wert: e.target.value })}
                             onKeyDown={(e) => { if (e.key === "Enter") umbenennenSpeichern(); if (e.key === "Escape") setUmbenennen(null); }} />
                      <button style={S.mini} disabled={busy} onClick={umbenennenSpeichern}>✓</button>
                      <button style={S.mini} onClick={() => setUmbenennen(null)}>✕</button>
                    </>
                  ) : (
                    <code>{c.field}</code>
                  )}
                  {c.key === "PRI" && <span title="Primärschlüssel"> 🔑</span>}
                </td>
                <td style={S.td} title={c.kommentar || ""}>{c.gruppe || "—"}</td>
                <td style={{ ...S.td, color: "var(--color-gray-600)" }}>{c.type}</td>
                <td style={{ ...S.td, textAlign: "right", whiteSpace: "nowrap" }}>
                  {c.key !== "PRI" && (
                    <>
                      <button style={S.mini} onClick={() => setUmbenennen({ spalte: c.field, wert: c.field })}>umbenennen</button>
                      <button style={{ ...S.mini, color: "var(--color-error)" }} onClick={() => loeschVorschau(c.field)}>löschen</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {loeschen && (
        <div style={S.overlay} onClick={() => setLoeschen(null)}>
          <div style={S.dialog} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 6px" }}>Spalte „{loeschen.spalte}" löschen?</h3>
            <p style={{ fontSize: "0.88rem", margin: "0 0 10px" }}>
              Typ {loeschen.typ} · <strong>{loeschen.gefuellte_werte}</strong> gefüllte Werte.
              Sie wandern in den Papierkorb und lassen sich wiederherstellen (die Spalte steht danach hinten).
            </p>
            {loeschen.ansichten?.length > 0 && (
              <div style={S.err}>
                Diese Ansichten benutzen die Spalte: <strong>{loeschen.ansichten.join(", ")}</strong>.
                Sie würden nicht mehr funktionieren. Du kannst sie mit entfernen — ihr SQL steht danach im Protokoll.
              </div>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
              <button style={S.knopf} onClick={() => setLoeschen(null)}>Abbrechen</button>
              {loeschen.ansichten?.length > 0
                ? <button style={S.rot} disabled={busy} onClick={() => loeschenJetzt(true)}>Spalte und {loeschen.ansichten.length} Ansichten entfernen</button>
                : <button style={S.rot} disabled={busy} onClick={() => loeschenJetzt(false)}>Spalte löschen</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Links unter der Tabellenliste: neue Tabelle, Papierkorb, Protokoll ─── */
export function StrukturSeitenleiste({ B, onGeaendert }) {
  const [offen, setOffen] = useState(null);                // "neu" | "papierkorb" | "log"
  const [name, setName] = useState("");
  const [kommentar, setKommentar] = useState("");
  const [daten, setDaten] = useState([]);
  const [fehler, setFehler] = useState("");
  const [busy, setBusy] = useState(false);

  const laden = useCallback(async (was) => {
    setFehler("");
    try {
      if (was === "papierkorb") setDaten((await ruf(`${B}/struktur/papierkorb`)).eintraege || []);
      if (was === "log") setDaten((await ruf(`${B}/struktur/log?limit=80`)).log || []);
    } catch (e) { setFehler(String(e.message || e)); }
  }, [B]);

  const oeffnen = (was) => { setOffen(was); setDaten([]); if (was !== "neu") laden(was); };

  const anlegen = async () => {
    setBusy(true); setFehler("");
    try { await ruf(`${B}/struktur/table`, "POST", { name, kommentar: kommentar || null }); setName(""); setKommentar(""); setOffen(null); onGeaendert(); }
    catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  const zurueck = async (id) => {
    setBusy(true); setFehler("");
    try { await ruf(`${B}/struktur/papierkorb/${id}/wiederherstellen`, "POST"); await laden("papierkorb"); onGeaendert(); }
    catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  return (
    <div style={{ marginTop: 10, display: "flex", gap: 6, flexWrap: "wrap" }}>
      <button style={S.knopf} onClick={() => oeffnen("neu")}>＋ Neue Tabelle</button>
      <button style={S.knopf} onClick={() => oeffnen("papierkorb")}>🧺 Papierkorb</button>
      <button style={S.knopf} onClick={() => oeffnen("log")}>📜 Protokoll</button>

      {offen && (
        <div style={S.overlay} onClick={() => setOffen(null)}>
          <div style={{ ...S.dialog, width: offen === "neu" ? 460 : 820 }} onClick={(e) => e.stopPropagation()}>
            {fehler && <div style={S.err}>{fehler}</div>}
            {offen === "neu" && (
              <>
                <h3 style={{ margin: "0 0 10px" }}>Neue Tabelle</h3>
                <input style={{ ...S.input, width: "100%", marginBottom: 8 }} autoFocus placeholder="tabellenname" value={name}
                       onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) anlegen(); }} />
                <input style={{ ...S.input, width: "100%" }} placeholder="Wofür? (optional)" value={kommentar}
                       onChange={(e) => setKommentar(e.target.value)} />
                <p style={{ fontSize: "0.78rem", color: "var(--color-gray-600)" }}>
                  Sie bekommt automatisch <code>id</code>, <code>quelle</code>, <code>erstellt_am</code> und <code>geaendert_am</code>.
                  Weitere Spalten legst du danach über „Spalten" an.
                </p>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <button style={S.knopf} onClick={() => setOffen(null)}>Abbrechen</button>
                  <button style={S.prim} disabled={busy || !name.trim()} onClick={anlegen}>Anlegen</button>
                </div>
              </>
            )}
            {offen === "papierkorb" && (
              <>
                <h3 style={{ margin: "0 0 10px" }}>Papierkorb — gelöschte Spalten</h3>
                {daten.length === 0 && <p style={{ color: "var(--color-gray-600)" }}>Leer.</p>}
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <tbody>
                    {daten.map((e) => (
                      <tr key={e.id}>
                        <td style={S.td}><code>{e.tabelle}.{e.spalte}</code></td>
                        <td style={{ ...S.td, color: "var(--color-gray-600)" }}>{e.anzahl_werte} Werte</td>
                        <td style={{ ...S.td, color: "var(--color-gray-600)" }}>{String(e.geloescht_am).replace("T", " ").slice(0, 16)} · {e.geloescht_von}</td>
                        <td style={{ ...S.td, textAlign: "right" }}>
                          {e.wiederhergestellt_am
                            ? <span style={{ color: "var(--color-gray-500)" }}>wiederhergestellt</span>
                            : <button style={S.mini} disabled={busy} onClick={() => zurueck(e.id)}>wiederherstellen</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            {offen === "log" && (
              <>
                <h3 style={{ margin: "0 0 10px" }}>Protokoll der Strukturänderungen</h3>
                {daten.length === 0 && <p style={{ color: "var(--color-gray-600)" }}>Noch nichts geändert.</p>}
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <tbody>
                    {daten.map((e) => (
                      <tr key={e.id}>
                        <td style={{ ...S.td, whiteSpace: "nowrap", color: "var(--color-gray-600)" }}>{String(e.zeit).replace("T", " ").slice(0, 16)}</td>
                        <td style={S.td}>{e.wer}</td>
                        <td style={S.td}>{e.aktion}</td>
                        <td style={S.td}><code>{e.tabelle}{e.spalte ? "." + e.spalte : ""}</code></td>
                        <td style={{ ...S.td, color: "var(--color-gray-600)" }}>{e.details}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            {offen !== "neu" && (
              <div style={{ textAlign: "right", marginTop: 12 }}>
                <button style={S.knopf} onClick={() => setOffen(null)}>Schließen</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Tabelle „löschen" (= umbenennen) ───────────────────────────────────── */
export async function tabelleLoeschen(B, tabelle) {
  return ruf(`${B}/struktur/table/${tabelle}`, "DELETE");
}
